import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, Search, RefreshCw } from "lucide-react";
import { usePublicProfiles } from "@/hooks/usePublicProfiles";
import { PublicProfileCard } from "@/components/profile/PublicProfileCard";

type RoleFilter = "broker" | "consultant" | "partner";

export function PublicProfileDirectory({
  roles,
  lang = "en",
  title,
  onContact,
  onViewFull,
}: {
  roles: RoleFilter[];
  lang?: "en" | "fr";
  title?: string;
  onContact?: (uid: string) => void;
  onViewFull?: (uid: string) => void;
}) {
  const { profiles, loading, error, reload } = usePublicProfiles(roles);
  const [search, setSearch] = useState("");
  const [activeRole, setActiveRole] = useState<"all" | RoleFilter>("all");

  const roleLabels: Record<string, { en: string; fr: string }> = {
    all: { en: "All", fr: "Tous" },
    broker: { en: "Brokers", fr: "Courtiers" },
    consultant: { en: "Consultants", fr: "Consultants" },
    partner: { en: "Internal Partners", fr: "Partenaires Internes" },
  };

  const filtered = profiles.filter((p) => {
    const matchRole = activeRole === "all" || p.role === activeRole;
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      `${p.firstName} ${p.lastName}`.toLowerCase().includes(q) ||
      (p.firmName || "").toLowerCase().includes(q) ||
      (p.primarySector || "").toLowerCase().includes(q) ||
      (p.city || "").toLowerCase().includes(q);
    return matchRole && matchSearch;
  });

  return (
    <div className="space-y-4">
      {title && <h2 className="text-2xl font-bold tracking-tight">{title}</h2>}

      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-10"
            placeholder={lang === "fr" ? "Rechercher..." : "Search..."}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Button variant="outline" size="icon" onClick={reload} disabled={loading}>
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {roles.length > 1 && (
        <div className="flex flex-wrap gap-2">
          {(["all", ...roles] as Array<"all" | RoleFilter>).map((r) => (
            <Button
              key={r}
              variant={activeRole === r ? "default" : "outline"}
              size="sm"
              onClick={() => setActiveRole(r)}
            >
              {roleLabels[r][lang]}
            </Button>
          ))}
        </div>
      )}

      {loading && (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {error && (
        <p className="text-sm text-destructive text-center py-4">{error}</p>
      )}

      {!loading && !error && filtered.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-12">
          {lang === "fr" ? "Aucun profil trouvé." : "No profiles found."}
        </p>
      )}

      {!loading && !error && filtered.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((p) => (
            <PublicProfileCard
              key={p.uid}
              profile={p}
              lang={lang}
              onContact={onContact}
              onViewFull={onViewFull}
            />
          ))}
        </div>
      )}
    </div>
  );
}
